import { EmptyState } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useAuth } from '@/lib/auth/useAuth';

/**
 * The per-screen half of docs/05-ADMIN-DASHBOARD.md §2, whose table puts a
 * permission on every route.
 *
 * It refuses in place rather than redirecting. A person who followed a link
 * from a colleague should be told they lack the permission, not silently
 * dropped on the dashboard wondering whether the link was wrong — and a
 * redirect makes "you cannot see this" indistinguishable from "this does not
 * exist".
 *
 * This is not the authorisation. Every endpoint behind these screens is guarded
 * by a Policy; hiding the screen only saves the person a wasted click.
 */
export interface RequirePermissionProps {
  permission?: string | null;
  superAdminOnly?: boolean;
}

export function RequirePermission({ permission, superAdminOnly }: RequirePermissionProps) {
  const { t } = useTranslation();
  const { can, hasRole } = useAuth();

  const allowed = (!superAdminOnly || hasRole('super-admin')) && (!permission || can(permission));

  if (!allowed) {
    return (
      <EmptyState
        className="mx-auto max-w-md py-16 text-center"
        title={t('errors.forbiddenTitle')}
        description={t('errors.forbiddenBody')}
      />
    );
  }

  return <Outlet />;
}
